/**
 * Quotes (Equipment Good/Better/Best — the proposal-app experience, brought native +
 * sourced from the pricebook). A quote = up to 3 options (Best/Better/Good collections);
 * an option = lines pulled from the pricebook `item` catalog (equipment + accessories),
 * each snapshotting the item's model / manufacturer / photo / link / price so the quote
 * stays stable even if the pricebook later changes. Pegasus-native + audited (zzaudit).
 * Decisions locked 2026-06-28 (SESSION-LOG "EQUIPMENT-GBB BUILD KICKOFF").
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { pagedAll } from "@/lib/pagedAll";
import { isSyntheticPart, type Item } from "@/features/items/items";
import { familyHint, isRightSize, type SizingSuggestion } from "@/lib/sizing";

export type QuoteTier = "good" | "better" | "best";
/**
 * A line's "kind" = a category key from `pegasus_quote_category` (furnace, ac, heat_pump,
 * water_heater, mini_split, service_repair, …) or the special "custom" (one-off line).
 * Open string so the office can add categories in Settings without a code change.
 */
export type QuoteLineKind = string;

export interface Quote {
  id: string;
  custno: string | null;
  locno: string | null;
  title: string | null;
  quote_type: string;
  offering_mode: string;
  category_keys: string[] | null;
  status: string;
  accepted_option_id: string | null;
  recommended_option_id: string | null;
  dispatch_no: string | null;
  job_id: string | null;
  token: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  sent_at?: string | null;
  /** QUOTE-LIFECYCLE-01: after this moment the customer can't accept (DB-enforced). */
  expires_at?: string | null;
  /** How they chose to pay at acceptance — 'full' | 'monthly' (quote_accept). */
  payment_preference?: string | null;
  /** quote_accept's record; financing_plan_id = the plan they picked (PLAN-SHOW-01). */
  acceptance_json?: { financing_plan_id?: string | null } | null;
  /** The optional lines + Recommended Add-Ons the homeowner ticked at accept (line ids). */
  selected_addons?: string[] | null;
  /** QUOTE-POLISH-11: the note the HOMEOWNER sees (his app's "external notes"); `notes` stays office-only. */
  homeowner_note?: string | null;
  /** PROPOSAL-FLOW-02: staff last opened the customer view (presented / reviewed) — never the status. */
  presented_at?: string | null;
  /** PROPOSAL-FLOW-02: how the link last left — 'text' | 'email' | 'link' — to whom, by whom (display name). */
  sent_via?: string | null;
  sent_to?: string | null;
  sent_by?: string | null;
  /** QUOTE-FAV-01: a Favorite — a whole-proposal preset, hidden from every list. */
  is_template?: boolean;
  template_name?: string | null;
  /** QUOTE-PRACTICE-01: a training build — real builder, zero footprint. */
  is_practice?: boolean;
  /** QUOTE-POLISH-05: homeowner opens of the live link (his app's "6 views").
   *  A signed-in staff open (Present / office preview) never counts. */
  view_count?: number;
  last_viewed_at?: string | null;
  /** PLAN-OFFER-01: one Plan Offer for the whole proposal (replaces the per-option Protect
   *  toggle). base = the catalog plan; mode optional (the homeowner ticks it) | included;
   *  terms + default term; the first covered check; bundle = the install prompt's answer. */
  plan_offer_base_id?: string | null;
  plan_offer_mode?: "optional" | "included" | null;
  plan_offer_default_term?: number | null;
  plan_offer_terms?: number[] | null;
  plan_offer_first_check_season?: string | null;
  plan_offer_first_check_year?: number | null;
  plan_offer_bundle?: "none" | "discount" | "first_year_free" | null;
  plan_offer_bundle_amount?: number | null;
  /** PLAN-OFFER-SYSTEMS: how many systems the offered plan covers and which units (kind + room, never model/serial). */
  plan_offer_systems?: number | null;
  /** PROPOSAL-EQUIPMENT-02: the unit(s) a repair quote is about (equipment counters) — the homeowner page leads with them. */
  work_units?: string[] | null;
  /** BUILDER-FLOW-01: true while the quote names itself from its content; a rename sets it false. */
  title_is_auto?: boolean | null;
  plan_offer_covers?: Array<{ counter: string; kind: string; label: string; loc: string | null; on: boolean }> | null;
}
export interface QuoteOption {
  id: string;
  quote_id: string;
  tier: QuoteTier;
  label: string | null;
  brand: string | null;
  rebate_total: number;
  discount_total: number;
  financing_plan: string | null;
  financing_plan_id: string | null;
  rebate_utility_id: string | null;
  category_keys: string[] | null; // per-option slot scope (null = use the quote-level slots)
  // Protect-as-membership: this option enrolls the customer in a PLUS plan on accept.
  plan_base_id: string | null;        // pegasus_plan_price.id (kind=base); null = no membership
  plan_term: number | null;           // years
  first_check_season: string | null;  // 'heating' (fall/winter furnace) | 'cooling' (spring/summer A/C)
  first_check_year: number | null;    // year the first covered check lands
  /** QUOTE-POLISH-04: the collection's warranty as two year-pickers (parts / labor).
   *  Null = the standing default (10/5; Good 5/5) — see warrantyYears(). */
  warranty_parts_years: number | null;
  warranty_labor_years: number | null;
  sort: number;
}
export interface QuoteLine {
  id: string;
  option_id: string;
  kind: QuoteLineKind;
  item_id: string | null;
  description: string | null;
  model: string | null;
  manufacturer: string | null;
  image_url: string | null;
  website_url: string | null;
  qty: number;
  unit_price: number;
  discount: number;
  /** How the line rides for the homeowner: included / required = locked in the
   *  package · optional = in the package, they may take it off · recommended =
   *  on the add-on shelf, in nothing until they add it. (Priced or $0 is the
   *  OTHER axis: discount = full value means Included at $0.) */
  role: string;
  /** Legacy free-text warranty (the wizard's "10-Year Parts · 5-Year Labor"). */
  warranty: string | null;
  /** QUOTE-POLISH-07: THIS unit's warranty — copied from the pricebook item when
   *  the line is added (DB trigger), editable per unit. Null = fall back. */
  warranty_parts_years?: number | null;
  warranty_labor_years?: number | null;
  sort: number;
}
export type QuoteDiscountMode = "amount" | "percent";
export interface QuoteDiscount {
  id: string;
  option_id: string;
  label: string | null;
  mode: QuoteDiscountMode;
  value: number;
  sort: number;
}
/** QUOTE-POLISH-05: one itemized rebate on a collection — a utility's, or a
 *  custom-named one (manufacturer promo, Inflation Reduction Act credit…). Any
 *  number per collection (CenterPoint for the furnace AND Xcel for the A/C is
 *  the normal Minnesota job). The option's rebate_total / rebate_utility_id
 *  mirror these rows automatically (DB trigger) — rows are the truth. */
export interface QuoteRebate {
  id: string;
  option_id: string;
  utility_id: string | null;
  custom_name: string | null;
  amount: number;
  sort: number;
}
export type QuoteOptionFull = QuoteOption & { lines: QuoteLine[]; discounts: QuoteDiscount[]; rebates: QuoteRebate[] };
export interface QuoteFull {
  quote: Quote;
  options: QuoteOptionFull[];
}

// Tiers render Best → Better → Good (high-to-low, à la the proposal app).
export const TIER_ORDER: QuoteTier[] = ["best", "better", "good"];
export const TIER_LABEL: Record<QuoteTier, string> = {
  best: "Best Collection",
  better: "Better Collection",
  good: "Good Collection",
};
// Sedgwick's two shop brands (the Lennox ↔ Armstrong toggle from the proposal app).
export const QUOTE_BRANDS = ["Lennox", "Armstrong"] as const;
const DEFAULT_BRAND = "Lennox";

export const KIND_LABEL: Record<QuoteLineKind, string> = {
  furnace: "Furnace",
  ac: "Air Conditioner",
  heat_pump: "Heat Pump",
  accessory: "Accessory",
  custom: "Custom",
};

/** One line's price: qty × unit price − line discount (never negative). */
export function lineTotal(l: QuoteLine): number {
  return Math.max(0, l.qty * l.unit_price - l.discount);
}
/** One named discount → a dollar amount ($ as-is; % resolved against the line subtotal). */
export function resolveDiscount(d: QuoteDiscount, subtotal: number): number {
  const v = Number(d.value) || 0;
  return d.mode === "percent" ? Math.max(0, subtotal * (v / 100)) : Math.max(0, v);
}
/** All IN-THE-PRICE lines added up (before rebate/discounts). The package is
 *  everything on it — included, required AND optional lines (QUOTE-POLISH-13,
 *  Seth 2026-10-16: Required ↔ Optional must never move the price; Optional
 *  only means the homeowner may take it OFF on the proposal, and the price
 *  drops then). Recommended Add-Ons are the one opt-in: they live on the
 *  shelf below the packages and price only when the homeowner adds one. */
export function optionSubtotal(o: QuoteOptionFull): number {
  return o.lines.reduce((s, l) => (l.role === "recommended" ? s : s + lineTotal(l)), 0);
}
/** Every named discount (% on the subtotal) + the legacy flat discount_total. */
export function optionDiscountTotal(o: QuoteOptionFull): number {
  const sub = optionSubtotal(o);
  const named = (o.discounts ?? []).reduce((s, d) => s + resolveDiscount(d, sub), 0);
  return named + (o.discount_total ?? 0);
}
/** The amount DUE TO SEDGWICK: all lines (included accessories net to $0 via
 *  their line discount), less every sale discount. Utility rebates are NOT
 *  subtracted — they're the customer's money back from the utility, shown as
 *  net savings, never a reduction of the contract (Seth 2026-08-27). */
export function optionTotal(o: QuoteOptionFull): number {
  return Math.max(0, optionSubtotal(o) - optionDiscountTotal(o));
}
/** Net investment after rebates — the sales-side "true bottom line" display. */
export function optionNet(o: QuoteOptionFull): number {
  return Math.max(0, optionTotal(o) - (o.rebate_total ?? 0));
}

export function useCustomerQuotes(custno: string | null) {
  return useQuery({
    queryKey: ["quotes", custno],
    enabled: !!custno,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<Quote[]> => {
      const { data } = await supabase
        .from("pegasus_quote")
        .select("*")
        .eq("custno", custno!)
        // Presets and practice builds never surface on a customer.
        .eq("is_template", false)
        .eq("is_practice", false)
        .order("created_at", { ascending: false });
      return (data ?? []) as Quote[];
    },
  });
}

export function useQuote(id: string | null) {
  return useQuery({
    queryKey: ["quote", id],
    enabled: !!id,
    queryFn: async (): Promise<QuoteFull | null> => {
      const { data: quote } = await supabase.from("pegasus_quote").select("*").eq("id", id!).maybeSingle();
      if (!quote) return null;
      const { data: options } = await supabase
        .from("pegasus_quote_option")
        .select("*")
        .eq("quote_id", id!)
        .order("sort");
      const optList = (options ?? []) as QuoteOption[];
      const optIds = optList.map((o) => o.id);
      const { data: lines } = optIds.length
        ? await supabase.from("pegasus_quote_line").select("*").in("option_id", optIds).order("sort")
        : { data: [] as QuoteLine[] };
      const byOpt = new Map<string, QuoteLine[]>();
      for (const l of (lines ?? []) as QuoteLine[]) {
        const arr = byOpt.get(l.option_id) ?? [];
        arr.push(l);
        byOpt.set(l.option_id, arr);
      }
      const { data: discounts } = optIds.length
        ? await supabase.from("pegasus_quote_discount").select("*").in("option_id", optIds).order("sort")
        : { data: [] as QuoteDiscount[] };
      const discByOpt = new Map<string, QuoteDiscount[]>();
      for (const d of (discounts ?? []) as QuoteDiscount[]) {
        const arr = discByOpt.get(d.option_id) ?? [];
        arr.push(d);
        discByOpt.set(d.option_id, arr);
      }
      // QUOTE-POLISH-05: itemized rebates ride with the option (the wizard
      // already wrote them this way; the office builder now reads/edits them too).
      const { data: rebates } = optIds.length
        ? await supabase.from("pegasus_quote_rebate").select("*").in("option_id", optIds).order("sort")
        : { data: [] as QuoteRebate[] };
      const rebByOpt = new Map<string, QuoteRebate[]>();
      for (const r of (rebates ?? []) as QuoteRebate[]) {
        const arr = rebByOpt.get(r.option_id) ?? [];
        arr.push(r);
        rebByOpt.set(r.option_id, arr);
      }
      return {
        quote: quote as Quote,
        options: optList.map((o) => ({
          ...o,
          lines: byOpt.get(o.id) ?? [],
          discounts: discByOpt.get(o.id) ?? [],
          rebates: rebByOpt.get(o.id) ?? [],
        })),
      };
    },
  });
}

/**
 * Create a fresh quote. `offering_mode` decides the shape: "tiered" pre-seeds the 3
 * Best/Better/Good collections (a system replacement); "single" seeds ONE clean offering
 * (a water-heater-only / mini-split-only proposal). `quote_type` is install or service.
 */
export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      custno: string | null;
      locno: string | null;
      title?: string;
      dispatch_no?: string | null;
      brand?: string;
      quote_type?: string;
      offering_mode?: "tiered" | "single";
      category_keys?: string[] | null;
    }): Promise<string> => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      // Service calls default to a single repair (options are opt-in, never forced);
      // installs default to the Good/Better/Best trio.
      const offering = v.offering_mode ?? ((v.quote_type ?? "install") === "service" ? "single" : "tiered");
      const { data: quote, error } = await supabase
        .from("pegasus_quote")
        .insert({
          custno: v.custno,
          locno: v.locno,
          title: v.title ?? (offering === "single" ? "New quote" : "New system quote"),
          title_is_auto: true,
          quote_type: v.quote_type ?? "install",
          offering_mode: offering,
          category_keys: v.category_keys ?? null,
          status: "draft",
          dispatch_no: v.dispatch_no ?? null,
          created_by: email,
        })
        .select("id")
        .single();
      if (error) throw error;
      const quoteId = (quote as { id: string }).id;
      const brand = v.brand ?? DEFAULT_BRAND;
      // Single offering = one option; tiered = the Best/Better/Good trio.
      const rows =
        offering === "single"
          ? [{ quote_id: quoteId, tier: "best" as QuoteTier, label: "Your Quote", brand, sort: 0 }]
          : TIER_ORDER.map((t, i) => ({ quote_id: quoteId, tier: t, label: TIER_LABEL[t], brand, sort: i }));
      const { error: oe } = await supabase.from("pegasus_quote_option").insert(rows);
      if (oe) throw oe;
      return quoteId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
    },
  });
}

/** Patch quote-level fields (type, offering shape, the per-quote category set, title/notes). */
export function useUpdateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      v: { id: string } & Partial<Pick<Quote, "title" | "notes" | "homeowner_note" | "quote_type" | "offering_mode" | "category_keys" | "recommended_option_id" | "is_practice"
        | "plan_offer_base_id" | "plan_offer_mode" | "plan_offer_default_term" | "plan_offer_terms" | "plan_offer_first_check_season" | "plan_offer_first_check_year" | "plan_offer_bundle" | "plan_offer_bundle_amount" | "plan_offer_systems" | "plan_offer_covers" | "work_units" | "title_is_auto">>,
    ) => {
      const { id, ...patch } = v;
      const { error } = await supabase
        .from("pegasus_quote")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
      qc.invalidateQueries({ queryKey: ["quote_list"] });
    },
  });
}

/** Add a fresh option (collection) to a quote — for going single → tiered, or a 2nd offering. */
export function useAddQuoteOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { quote_id: string; tier?: QuoteTier; label?: string; brand?: string; sort?: number; category_keys?: string[] | null }) => {
      const { error } = await supabase.from("pegasus_quote_option").insert({
        quote_id: v.quote_id,
        tier: v.tier ?? "good",
        label: v.label ?? "Option",
        brand: v.brand ?? DEFAULT_BRAND,
        sort: v.sort ?? 0,
        category_keys: v.category_keys ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote"] }),
  });
}

/** Remove an option (collection) and its lines/discounts (FK cascade) from a quote. */
export function useRemoveQuoteOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_quote_option").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote"] }),
  });
}

/** PROPOSAL-VISION-02 → QUOTE-POLISH-01: fill ONE collection from a package.
 *  The package's lines are snapshotted as quote lines; `inc` nets to $0 the
 *  quote-line way (discount = full value → the proposal strikes it, says
 *  Included). The collection wears the package's brand + (when unwritten)
 *  blurb. New in the polish pass:
 *  · `replace: true` clears the collection first (two-tap confirm in the UI),
 *    so a package can be re-applied / swapped — chips never go dead.
 *  · `sizing` makes the fill SIZE-AWARE: each equipment line looks for the
 *    same brand + tier sibling that matches the Sizing Assist suggestion
 *    (closest model family first) and swaps model + price. The 3-ton seed
 *    package becomes the 4-ton/110K build on a 4-ton house, automatically.
 *  · Brand/blurb dress-up is NON-FATAL and the cache refreshes in onSettled —
 *    the old version could insert lines, then die on the patch and never
 *    refetch, which read as "the chips stopped working". */
export function useApplyQuotePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      option: QuoteOptionFull;
      pkg: { label: string; blurb: string | null; lines: Array<{ item_id: string | null; kind: string; description: string; model: string | null; manufacturer: string | null; image_url: string | null; qty: number; unit_price: number; role: string; inc: boolean; warranty: string | null }> };
      replace?: boolean;
      sizing?: SizingSuggestion | null;
    }): Promise<{ swapped: string[] }> => {
      if (v.replace) {
        const { error } = await supabase.from("pegasus_quote_line").delete().eq("option_id", v.option.id);
        if (error) throw error;
      }
      const base = v.replace ? 0 : v.option.lines.length;
      const rows = v.pkg.lines.map((l, i) => ({
        option_id: v.option.id,
        kind: l.kind || "custom",
        item_id: l.item_id,
        description: l.description,
        model: l.model,
        manufacturer: l.manufacturer,
        image_url: l.image_url,
        website_url: null as string | null,
        qty: l.qty || 1,
        unit_price: l.unit_price || 0,
        discount: l.inc ? (l.qty || 1) * (l.unit_price || 0) : 0,
        role: l.role || "required",
        warranty: l.warranty,
        sort: base + i,
      }));

      // ── SIZE-AWARE FILL: swap equipment lines to the right-size sibling ──
      const swapped: string[] = [];
      const equipRows = rows.filter((r) => r.kind !== "accessory" && r.kind !== "custom" && r.item_id);
      if (v.sizing && equipRows.length) {
        const { data: origs } = await supabase
          .from("item")
          .select("*")
          .in("id", equipRows.map((r) => r.item_id as string));
        const byId = new Map(((origs ?? []) as Item[]).map((o) => [o.id, o]));
        for (const r of equipRows) {
          const it = byId.get(r.item_id as string);
          if (!it?.equip_type) continue;
          const hint = familyHint(it.equip_type);
          if (!hint) continue; // only sized families (furnace / AC / heat pump)
          if (isRightSize(it.model ?? it.part_number, v.sizing, hint)) continue; // already right
          let q = supabase.from("item").select("*").eq("archived", false).not("inactive", "is", true).eq("equip_type", it.equip_type);
          if (it.manufacturer) q = q.ilike("manufacturer", it.manufacturer);
          if (it.tier) q = q.eq("tier", it.tier);
          const { data: cands } = await q.limit(200);
          const fits = ((cands ?? []) as Item[]).filter(
            (c) => c.id !== it.id && isRightSize(c.model ?? c.part_number, v.sizing ?? null, hint),
          );
          if (!fits.length) continue;
          const prefixLen = (a: string | null, b: string | null) => {
            const x = (a ?? "").toUpperCase(), y = (b ?? "").toUpperCase();
            let n = 0;
            while (n < x.length && n < y.length && x[n] === y[n]) n++;
            return n;
          };
          const priceOf = (c: Item) => Number(c.sell_price ?? c.base_price ?? 0);
          const origPrice = priceOf(it);
          // Same model family first (SLP99UH070 → SLP99UH110), then the price
          // nearest the original breaks ties.
          fits.sort(
            (a, b) =>
              prefixLen(b.model ?? b.part_number, it.model ?? it.part_number) - prefixLen(a.model ?? a.part_number, it.model ?? it.part_number) ||
              Math.abs(priceOf(a) - origPrice) - Math.abs(priceOf(b) - origPrice),
          );
          const w = fits[0];
          swapped.push(`${it.model ?? r.description} → ${w.model ?? w.part_number ?? w.description ?? "right-size match"}`);
          r.item_id = w.id;
          r.description = w.description ?? r.description;
          r.model = w.model ?? w.part_number ?? r.model;
          r.manufacturer = w.manufacturer ?? r.manufacturer;
          r.image_url = w.image_url ?? r.image_url;
          const p = priceOf(w);
          if (p > 0) {
            r.unit_price = p;
            if (r.discount > 0) r.discount = (r.qty || 1) * p; // keep the $0-Included strike honest
          }
        }
      }

      if (rows.length) {
        const { error } = await supabase.from("pegasus_quote_line").insert(rows);
        if (error) throw error;
      }
      // Dress-up (brand + blurb) must never kill an apply that already landed.
      try {
        const mfg = v.pkg.lines.find((l) => l.kind !== "accessory" && l.manufacturer)?.manufacturer ?? null;
        const brand = mfg ? mfg.charAt(0).toUpperCase() + mfg.slice(1).toLowerCase() : null;
        const patch: Record<string, unknown> = {};
        if (brand) patch.brand = brand;
        if (v.pkg.blurb) {
          const { data: cur } = await supabase.from("pegasus_quote_option").select("description").eq("id", v.option.id).maybeSingle();
          if (!(((cur as { description?: string | null } | null)?.description ?? "").trim()) || v.replace) patch.description = v.pkg.blurb;
        }
        if (Object.keys(patch).length) {
          await supabase.from("pegasus_quote_option").update(patch).eq("id", v.option.id);
        }
      } catch {
        /* non-fatal — the lines are in; the office can set brand/blurb by hand */
      }
      return { swapped };
    },
    // onSettled (not onSuccess): even a failed apply refetches, so the shelf
    // always reflects what actually landed.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["quote"] });
    },
  });
}

/** QUOTE-POLISH-01: copy one collection's money story (utility rebate +
 *  named discounts + financing plan) and/or its accessories onto every other
 *  collection — "fill out Good once, mirror it across Better and Best."
 *  Discounts REPLACE the targets' (a copy means "make them match"); accessory
 *  copies skip anything the target already has (same item, or same name when
 *  there's no pricebook link), so it never doubles up. */
export function useCopyOptionExtras() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      from: QuoteOptionFull;
      targets: QuoteOptionFull[];
      money?: boolean;
      accessories?: boolean;
      /** Category keys that are equipment slots (never copied). */
      equipKeys: string[];
    }) => {
      const eq = new Set(v.equipKeys);
      for (const t of v.targets) {
        if (t.id === v.from.id) continue;
        if (v.money) {
          const { error } = await supabase
            .from("pegasus_quote_option")
            .update({
              rebate_utility_id: v.from.rebate_utility_id,
              rebate_total: v.from.rebate_total ?? 0,
              financing_plan_id: v.from.financing_plan_id,
            })
            .eq("id", t.id);
          if (error) throw error;
          // QUOTE-POLISH-05: the itemized rebate rows copy too (they're the
          // truth; the option mirror above is refreshed by the DB trigger).
          const { error: rde } = await supabase.from("pegasus_quote_rebate").delete().eq("option_id", t.id);
          if (rde) throw rde;
          const srcRebates = v.from.rebates ?? [];
          if (srcRebates.length) {
            const { error: rie } = await supabase.from("pegasus_quote_rebate").insert(
              srcRebates.map((r, i) => ({ option_id: t.id, utility_id: r.utility_id, custom_name: r.custom_name, amount: r.amount, sort: i })),
            );
            if (rie) throw rie;
          }
          const { error: de } = await supabase.from("pegasus_quote_discount").delete().eq("option_id", t.id);
          if (de) throw de;
          const srcDiscounts = v.from.discounts ?? [];
          if (srcDiscounts.length) {
            const { error: ie } = await supabase.from("pegasus_quote_discount").insert(
              srcDiscounts.map((d, i) => ({ option_id: t.id, label: d.label, mode: d.mode, value: d.value, sort: i })),
            );
            if (ie) throw ie;
          }
        }
        if (v.accessories) {
          const src = v.from.lines.filter((l) => !eq.has(l.kind) && l.kind !== "custom");
          const have = new Set(
            t.lines.map((l) => l.item_id ?? `d:${(l.description ?? "").trim().toLowerCase()}`),
          );
          const fresh = src.filter((l) => !have.has(l.item_id ?? `d:${(l.description ?? "").trim().toLowerCase()}`));
          if (fresh.length) {
            const baseSort = t.lines.length ? Math.max(...t.lines.map((l) => l.sort)) + 1 : 0;
            const { error } = await supabase.from("pegasus_quote_line").insert(
              fresh.map((l, i) => ({
                option_id: t.id,
                kind: l.kind,
                item_id: l.item_id,
                description: l.description,
                model: l.model,
                manufacturer: l.manufacturer,
                image_url: l.image_url,
                website_url: l.website_url,
                qty: l.qty,
                unit_price: l.unit_price,
                discount: l.discount,
                role: l.role ?? "included",
                warranty: l.warranty,
                sort: baseSort + i,
              })),
            );
            if (error) throw error;
          }
        }
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["quote"] });
    },
  });
}

/** Add an equipment/accessory line to an option, snapshotting the pricebook item. */
export function useAddQuoteLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      option_id: string;
      kind: QuoteLineKind;
      item?: Item | null;
      description?: string;
      unit_price?: number;
      sort?: number;
      /** QUOTE-POLISH-03: how the line rides for the homeowner (default: in the price). */
      role?: "included" | "required" | "optional" | "recommended";
      /** Included-at-$0: the value shows struck, the math nets to zero (discount = full value). */
      inc?: boolean;
    }) => {
      const it = v.item ?? null;
      const price = v.unit_price ?? Number(it?.sell_price ?? it?.base_price ?? 0);
      const { error } = await supabase.from("pegasus_quote_line").insert({
        option_id: v.option_id,
        kind: v.kind,
        item_id: it?.id ?? null,
        description: v.description ?? it?.description ?? null,
        // Never snapshot a synthetic import key (PA-…) as the line's "model" —
        // it would ride onto the homeowner proposal looking like one.
        model: it?.model ?? (isSyntheticPart(it?.part_number) ? null : (it?.part_number ?? null)),
        manufacturer: it?.manufacturer ?? null,
        image_url: it?.image_url ?? null,
        website_url: it?.website_url ?? null,
        qty: 1,
        unit_price: price,
        discount: v.inc ? price : 0,
        role: v.role ?? "included",
        sort: v.sort ?? 0,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
    },
  });
}

export function useUpdateQuoteLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      v: { id: string } & Partial<Pick<QuoteLine, "qty" | "unit_price" | "discount" | "description" | "role" | "warranty" | "warranty_parts_years" | "warranty_labor_years">>,
    ) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("pegasus_quote_line").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
    },
  });
}

export function useRemoveQuoteLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await supabase.from("pegasus_quote_line").delete().eq("id", id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
    },
  });
}

/** Flip a collection's brand (Lennox ↔ Armstrong) — the proposal-app brand toggle. */
export function useSetOptionBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; brand: string }) => {
      const { error } = await supabase.from("pegasus_quote_option").update({ brand: v.brand }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
    },
  });
}

/** Option-level edits — label, utility rebate, special/discount, financing plan. */
export function useUpdateQuoteOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      v: { id: string } & Partial<Pick<QuoteOption, "label" | "tier" | "rebate_total" | "discount_total" | "financing_plan" | "financing_plan_id" | "rebate_utility_id" | "category_keys" | "plan_base_id" | "plan_term" | "first_check_season" | "first_check_year" | "warranty_parts_years" | "warranty_labor_years">>,
    ) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("pegasus_quote_option").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
    },
  });
}

/**
 * Membership status for the Protect column — the customer's current PLUS
 * coverage horizon (latest active expiry across ESC mirror_serv_agr + Pegasus
 * pegasus_plan_sale), classified so the tech sees whether Protect means enroll,
 * renew/extend, or "already covered". Read-only. Mirrors the accept trigger's
 * 90-day "expiring soon" window so the picker framing matches what accept does.
 */
export type MembershipState = "none" | "active" | "expiring" | "lapsed";
export interface MembershipStatus {
  state: MembershipState;
  horizon: string | null; // ISO date of latest active expiry (null = never a member)
  daysLeft: number | null;
}
export function useCustomerMembershipStatus(custno: string | null | undefined) {
  return useQuery({
    // BUILDER-FLOW-01 fix: this shape ({state, horizon}) shared a cache key with agreements/api
    // useMembershipStatus (the RPC row) — whichever loaded first answered for both, and the
    // builder read "Lapsed" on a member. Its own key now.
    queryKey: ["quote_membership_status", custno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<MembershipStatus> => {
      const [esc, peg, ful] = await Promise.all([
        supabase.from("mirror_serv_agr").select("expire_date").eq("custno", custno!).eq("sa_inactive", 0).not("expire_date", "is", null).order("expire_date", { ascending: false }).limit(1),
        // PLAN-STATUS-ACTIVE-01 (34i101): 'sold' is the plan in force — the value every reader counts.
        supabase.from("pegasus_plan_sale").select("expire_date").eq("custno", custno!).eq("status", "sold").not("expire_date", "is", null).order("expire_date", { ascending: false }).limit(1),
        // MEMBERSHIP-ESC-FULFILLED: an ESC plan set inactive after its final tune-up is
        // still paid through its expire date — the SQL lifecycle knows which ones.
        supabase.from("v_membership_plan").select("expire_date").eq("custno", custno!).eq("source", "esc").eq("status", "fulfilled").order("expire_date", { ascending: false }).limit(1),
      ]);
      const dates = [
        (esc.data?.[0] as { expire_date: string } | undefined)?.expire_date,
        (peg.data?.[0] as { expire_date: string } | undefined)?.expire_date,
        (ful.data?.[0] as { expire_date: string } | undefined)?.expire_date,
      ].filter(Boolean) as string[];
      if (dates.length === 0) return { state: "none", horizon: null, daysLeft: null };
      const horizon = dates.sort().at(-1)!;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const daysLeft = Math.round((new Date(horizon + "T00:00:00").getTime() - today.getTime()) / 86400000);
      const state: MembershipState = daysLeft < 0 ? "lapsed" : daysLeft <= 90 ? "expiring" : "active";
      return { state, horizon, daysLeft };
    },
  });
}

/**
 * Repair-vs-replace facts for the service quote's customer — their own past
 * service-repair spend + the system's age, from `repair_replace_summary`. The
 * builder contrasts it with the Replace option's price so the case for a new
 * system makes itself. Facts only; the recommendation is computed in the UI.
 */
export interface RepairReplace {
  recent_years: number;
  recent_spend: number;
  recent_visits: number;
  lifetime_spend: number;
  lifetime_visits: number;
  last_repair_date: string | null;
  first_repair_date: string | null;
  oldest_install_date: string | null;
  system_age_years: number | null;
  oldest_unit: string | null;
  oldest_unit_type: string | null;
}
export function useRepairReplace(custno: string | null | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["repair_replace", custno, locno ?? "all"],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<RepairReplace | null> => {
      const { data, error } = await supabase.rpc("repair_replace_summary", { p_custno: custno, p_locno: locno ?? null });
      if (error) throw error;
      return (data ?? null) as RepairReplace | null;
    },
  });
}

/** Add a named discount (% or $) to an option — the proposal-app "custom-named discount". */
export function useAddQuoteDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { option_id: string; label?: string; mode?: QuoteDiscountMode; value?: number; sort?: number }) => {
      const { error } = await supabase.from("pegasus_quote_discount").insert({
        option_id: v.option_id,
        label: v.label ?? "Discount",
        mode: v.mode ?? "amount",
        value: v.value ?? 0,
        sort: v.sort ?? 0,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote"] }),
  });
}

export function useUpdateQuoteDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<Pick<QuoteDiscount, "label" | "mode" | "value">>) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("pegasus_quote_discount").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote"] }),
  });
}

export function useRemoveQuoteDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await supabase.from("pegasus_quote_discount").delete().eq("id", id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote"] }),
  });
}

/* ── QUOTE-POLISH-05: itemized rebates (office builder parity with the wizard) ──
   Any number per collection. The option's rebate_total / rebate_utility_id are
   mirrored by the zz_quote_rebate_mirror trigger, so every reader that still
   looks at the option (lists, footer, SalePacket) stays right for free. */
export function useAddQuoteRebate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { option_id: string; utility_id?: string | null; custom_name?: string | null; amount?: number; sort?: number }) => {
      const { error } = await supabase.from("pegasus_quote_rebate").insert({
        option_id: v.option_id,
        utility_id: v.utility_id ?? null,
        custom_name: v.utility_id ? null : (v.custom_name ?? "Rebate"),
        amount: v.amount ?? 0,
        sort: v.sort ?? 0,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote"] }),
  });
}
export function useUpdateQuoteRebate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<Pick<QuoteRebate, "utility_id" | "custom_name" | "amount">>) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("pegasus_quote_rebate").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote"] }),
  });
}
export function useRemoveQuoteRebate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_quote_rebate").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote"] }),
  });
}

// ---- Office Proposals list (every quote) + status lifecycle ----

/** The proposal lifecycle the office drives. `viewed` is derived (sent + the homeowner opened it). */
export const QUOTE_STATUSES = ["draft", "sent", "viewed", "accepted", "declined", "expired"] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];
export const QUOTE_STATUS_LABEL: Record<string, string> = {
  draft: "Draft", sent: "Sent", viewed: "Viewed", accepted: "Accepted", declined: "Declined", expired: "Expired",
};

/** QUOTE-LIFECYCLE-01: one place answers "can this still be accepted?" */
export function quoteExpiry(q: Pick<Quote, "status" | "expires_at" | "sent_at" | "created_at">): {
  expiresAt: Date | null; expired: boolean; daysLeft: number | null;
} {
  if (q.status === "accepted" || q.status === "declined") return { expiresAt: null, expired: false, daysLeft: null };
  const at = q.expires_at
    ? new Date(q.expires_at)
    : q.sent_at ? new Date(new Date(q.sent_at).getTime() + 30 * 864e5) : null; // legacy sends: the DB's old 30-day rule
  if (!at) return { expiresAt: null, expired: q.status === "expired", daysLeft: null };
  const ms = at.getTime() - Date.now();
  return { expiresAt: at, expired: q.status === "expired" || ms <= 0, daysLeft: Math.ceil(ms / 864e5) };
}

/** A row of the `v_quote_list` aggregate view — one per quote, customer + headline total resolved in SQL. */
export interface QuoteListRow {
  id: string;
  custno: string | null;
  locno: string | null;
  title: string | null;
  status: string;
  quote_type: string;
  dispatch_no: string | null;
  job_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  viewed_at: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  accepted_option_id: string | null;
  headline_total: number;
  option_count: number;
  customer_name: string | null;
  /** OFFICE-NOTE-01: provenance stamp + the tech's note to the office. */
  notes?: string | null;
  /** QUOTE-DECLINE-01: the homeowner's own words when they said "not now". */
  declined_reason?: string | null;
  /** QUOTE-ASK-01: homeowner questions nobody has answered yet. */
  open_questions?: number;
  /** QUOTE-POLISH-05: how many times the homeowner opened the link, and when last. */
  view_count?: number;
  last_viewed_at?: string | null;
}

/* ── QUOTE-ASK-01: the homeowner's question channel ─────────────────────────
   One message from the proposal page (public RPC quote_message_send) → a row
   here → the quote's Activity shows it with reply-by-text/email; the lanes and
   the Today desk badge "question waiting" until someone marks it replied. */
export interface QuoteMessage {
  id: string;
  quote_id: string;
  direction: "in" | "out";
  body: string;
  contact: string | null;
  actor: string | null;
  created_at: string;
  read_at: string | null;
  replied_at: string | null;
  replied_by: string | null;
}
export function useQuoteMessages(quoteId: string | null) {
  return useQuery({
    queryKey: ["quote_messages", quoteId],
    enabled: !!quoteId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<QuoteMessage[]> => {
      const { data, error } = await supabase
        .from("pegasus_quote_message")
        .select("*")
        .eq("quote_id", quoteId!)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as QuoteMessage[];
    },
  });
}
export function useMarkMessageReplied() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; quoteId: string; replied: boolean }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_quote_message")
        .update(v.replied
          ? { replied_at: new Date().toISOString(), replied_by: u?.user?.email ?? null, read_at: new Date().toISOString() }
          : { replied_at: null, replied_by: null })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["quote_messages", v.quoteId] });
      void qc.invalidateQueries({ queryKey: ["quote_list"] });
      void qc.invalidateQueries({ queryKey: ["today_open_proposals"] });
    },
  });
}
/** QUOTE-POLISH-05: an office reply (sent by text or email) is ALSO written to
 *  the thread as an outbound message, so the homeowner page shows the whole
 *  conversation when they come back — his app's "Questions & Collaboration". */
export function useRecordQuoteReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { quoteId: string; body: string; to: string | null }) => {
      const body = v.body.trim();
      if (!body) return;
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_quote_message").insert({
        quote_id: v.quoteId,
        direction: "out",
        body: body.slice(0, 2000),
        contact: v.to ? v.to.slice(0, 200) : null,
        actor: u?.user?.email ?? "office",
        read_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["quote_messages", v.quoteId] });
    },
  });
}

/** QUOTE-POLISH-04: the warranty a collection carries — its own year pickers when
 *  set, else the standing default (Good 5/5, Better & Best 10/5). One rule for
 *  the builder, the homeowner page and the breakdown. */
export function warrantyYears(o: { tier?: string | null; warranty_parts_years?: number | null; warranty_labor_years?: number | null } | null | undefined): { parts: number; labor: number; isDefault: boolean } {
  const defParts = o?.tier === "good" ? 5 : 10;
  const defLabor = 5;
  const parts = o?.warranty_parts_years ?? null;
  const labor = o?.warranty_labor_years ?? null;
  return { parts: parts ?? defParts, labor: labor ?? defLabor, isDefault: parts == null && labor == null };
}
export function warrantyLabel(o: Parameters<typeof warrantyYears>[0]): string {
  const w = warrantyYears(o);
  return `${w.parts}-Year Parts · ${w.labor}-Year Labor`;
}
/** @deprecated use warrantyLabel — kept for callers that only know the tier. */
export function defaultWarranty(tier: string | null | undefined): string {
  return warrantyLabel({ tier });
}

/** QUOTE-POLISH-07: the warranty ONE UNIT carries (Seth: "an SLP99 at 12/7 next
 *  to an ML13 at 5/2"). Resolution: the unit's own years → the wizard's legacy
 *  text on the line → the collection's years → the tier standard. `source`
 *  says which won, so the UI can offer "Standard" only when it's been changed. */
export const WARRANTY_TEXT_RE = /(\d+)-Year Parts · (\d+)-Year Labor/;
export function lineWarranty(
  l: { warranty?: string | null; warranty_parts_years?: number | null; warranty_labor_years?: number | null } | null | undefined,
  o: Parameters<typeof warrantyYears>[0],
): { parts: number; labor: number; source: "unit" | "text" | "collection" | "standard" } {
  const base = warrantyYears(o);
  if (l && (l.warranty_parts_years != null || l.warranty_labor_years != null)) {
    return { parts: l.warranty_parts_years ?? base.parts, labor: l.warranty_labor_years ?? base.labor, source: "unit" };
  }
  const m = l?.warranty ? WARRANTY_TEXT_RE.exec(l.warranty) : null;
  if (m) return { parts: Number(m[1]), labor: Number(m[2]), source: "text" };
  return { parts: base.parts, labor: base.labor, source: base.isDefault ? "standard" : "collection" };
}
export function lineWarrantyLabel(l: Parameters<typeof lineWarranty>[0], o: Parameters<typeof warrantyYears>[0]): string {
  // A legacy text that isn't in the X/Y shape (hand-typed) is shown as written.
  if (l && l.warranty_parts_years == null && l.warranty_labor_years == null && (l.warranty ?? "").trim() && !WARRANTY_TEXT_RE.test(l.warranty!)) return l.warranty!.trim();
  const w = lineWarranty(l, o);
  return `${w.parts}-Year Parts · ${w.labor}-Year Labor`;
}

/** Display status: a `sent` quote the homeowner has opened reads as `viewed` (derived, never stored). */
export function displayStatus(q: Pick<QuoteListRow, "status" | "viewed_at"> & { expires_at?: string | null }): string {
  // Past-expiry sent/viewed quotes READ as expired even before the nightly sweep flips them.
  if ((q.status === "sent" || q.status === "viewed") && q.expires_at && new Date(q.expires_at).getTime() < Date.now()) return "expired";
  if (q.status === "sent" && q.viewed_at) return "viewed";
  return q.status || "draft";
}

/** QUOTE-LIFECYCLE-01: give the customer more time — resets the clock from
 *  today (org proposal_valid_days) and revives an expired quote to 'sent'. */
export function useExtendQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; currentStatus: string }) => {
      const { data: s } = await supabase.from("org_setting").select("value").eq("key", "proposal_valid_days").maybeSingle();
      const days = Number((s as { value?: unknown } | null)?.value ?? 30) || 30;
      const patch: Record<string, unknown> = {
        expires_at: new Date(Date.now() + days * 864e5).toISOString(),
        updated_at: new Date().toISOString(),
      };
      if (v.currentStatus === "expired") patch.status = "sent";
      const { error } = await supabase.from("pegasus_quote").update(patch).eq("id", v.id);
      if (error) throw error;
      return days;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
      qc.invalidateQueries({ queryKey: ["quote_list"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
    },
  });
}

/** Clean an ESC-style "Last,First & Spouse 0001005" name → "First Last" for display. */
export function cleanCustomerName(raw: string | null): string {
  if (!raw) return "—";
  let s = raw.replace(/\s*\d{6,7}\s*$/, "").trim(); // drop trailing custno
  if (s.includes(",")) {
    const [last, rest] = s.split(",");
    s = `${(rest ?? "").trim()} ${last.trim()}`.trim();
  }
  return s || "—";
}

export function useQuoteList() {
  return useQuery({
    queryKey: ["quote_list"],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<QuoteListRow[]> =>
      // PAGEDALL-SWEEP (review 2026-11-02): the list used to stop at PostgREST's
      // silent 1,000-row cap — the thousand-and-first proposal simply vanished.
      pagedAll<QuoteListRow>(() =>
        supabase.from("v_quote_list").select("*").order("created_at", { ascending: false }).order("id", { ascending: true }),
      ),
  });
}

/** Set a quote's status from the office, stamping the matching lifecycle timestamp. */
export function useSetQuoteStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; status: QuoteStatus }) => {
      const now = new Date().toISOString();
      const patch: Record<string, unknown> = { status: v.status, updated_at: now };
      if (v.status === "sent") {
        patch.sent_at = now;
        // QUOTE-LIFECYCLE-01: sending starts the clock (org-configurable).
        const { data: s } = await supabase.from("org_setting").select("value").eq("key", "proposal_valid_days").maybeSingle();
        const days = Number((s as { value?: unknown } | null)?.value ?? 30) || 30;
        patch.expires_at = new Date(Date.now() + days * 864e5).toISOString();
      }
      if (v.status === "accepted") patch.accepted_at = now;
      const { error } = await supabase.from("pegasus_quote").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote_list"] });
      qc.invalidateQueries({ queryKey: ["quote"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["quote_events"] });
    },
  });
}

/** QUOTE-FIX-01 (Seth 2026-08-25 "I manually accepted a proposal - nothing
 *  pops up"): a manual accept needs to say WHICH option won — this stamps it
 *  (+ status accepted) so the Create-the-install-job flow lights up. */
export function useAcceptOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { quoteId: string; optionId: string }) => {
      const now = new Date().toISOString();
      const { error } = await supabase.from("pegasus_quote")
        .update({ accepted_option_id: v.optionId, status: "accepted", accepted_at: now, updated_at: now })
        .eq("id", v.quoteId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
      qc.invalidateQueries({ queryKey: ["quote_list"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["quote_events"] });
    },
  });
}

/** QUOTE-FIX-01: delete a proposal (options → lines → discounts → quote).
 *  A job created from it is NOT touched — the job survives on its own. */
export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: opts } = await supabase.from("pegasus_quote_option").select("id").eq("quote_id", id);
      const optIds = ((opts ?? []) as { id: string }[]).map((o) => o.id);
      if (optIds.length) {
        await supabase.from("pegasus_quote_discount").delete().in("option_id", optIds);
        await supabase.from("pegasus_quote_line").delete().in("option_id", optIds);
        await supabase.from("pegasus_quote_option").delete().in("id", optIds);
      }
      const { error } = await supabase.from("pegasus_quote").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote_list"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["job_quote"] });
    },
  });
}

/** Does the job a quote points at still exist? (Deleting a job used to leave
 *  the quote pointing at nothing — "job not found".) */
export function useQuoteJobExists(jobId: string | null) {
  return useQuery({
    queryKey: ["quote_job_exists", jobId],
    enabled: !!jobId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<boolean> => {
      const { data } = await supabase.from("pegasus_job").select("id").eq("id", jobId!).maybeSingle();
      return !!data;
    },
  });
}

/** CALLBACK-01 (Seth 2026-08-26: "customer calls in a week and wants the good
 *  repair option — where would the dispatcher go... how would they schedule
 *  it?"): accepting a repair books a RETURN VISIT into the Board's queue.
 *  The dispatcher finds the proposal on the customer's 360 (or Proposals),
 *  taps the option, taps Book — the ticket lands in Return Visits with the
 *  approved price + parts list, and gets scheduled like any other call. */
export function useBookRepairVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { quote: Quote; option: QuoteOptionFull }): Promise<void> => {
      const custno = v.quote.custno;
      if (!custno) throw new Error("Attach a customer to the quote first.");
      const { data: u } = await supabase.auth.getUser();
      const [{ data: cust }, { data: locs }] = await Promise.all([
        supabase.from("mirror_customer").select("fullname, phone1").eq("custno", custno).maybeSingle(),
        supabase.from("mirror_location").select("locno, add1, city, phone1").eq("custno", custno).order("locno"),
      ]);
      const c = cust as { fullname?: string | null; phone1?: string | null } | null;
      const locRows = (locs ?? []) as { locno: string; add1: string | null; city: string | null; phone1: string | null }[];
      const loc = (v.quote.locno ? locRows.find((l) => l.locno === v.quote.locno) : null) ?? locRows[0] ?? null;
      const total = optionTotal(v.option);
      const partsLine = v.option.lines
        .map((l) => `${(l.qty || 1) > 1 ? `${l.qty}× ` : ""}${l.description ?? l.model ?? "part"}`)
        .join(", ");
      const { error } = await supabase.from("pegasus_dispatch").insert({
        lane_key: "return_visit",
        custno,
        locno: v.quote.locno ?? loc?.locno ?? null,
        customer_name: c?.fullname?.trim() || custno,
        address: loc?.add1 ?? null,
        city: loc?.city ?? null,
        phone: loc?.phone1 ?? c?.phone1 ?? null,
        summary: `Approved repair — ${v.quote.title?.trim() || "repair quote"} (${total.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })})`,
        notes: `Customer accepted the ${v.option.label || v.option.tier} repair option.` +
          (partsLine ? ` Parts: ${partsLine}.` : "") +
          (v.quote.dispatch_no ? ` Original call #${v.quote.dispatch_no}.` : ""),
        quoted_amount: total,
        origin_dispatch: v.quote.dispatch_no ?? null,
        source: "quote",
        created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pending_tickets"] }),
  });
}

// ---- QUOTE-FAV-01: Favorites — whole-proposal presets (Seth's proposal app:
// "Save as Favorite Template" / "Load Favorite"). A favorite is a pegasus_quote
// flagged is_template: no customer, no dispatch, hidden from every list by
// v_quote_list. Saving deep-copies the current build; loading deep-copies a
// favorite ONTO the open quote (its options are replaced, its customer kept).
// QUOTE-FAV-02 (Seth 2026-10-14): Favorites are PERSONAL — each user sees,
// names, edits and deletes their own (template_owner = auth.uid(), and the
// favorites_private RLS policy hides everyone else's). Packages stay the
// team-wide library. ----

export interface QuoteTemplate {
  id: string;
  template_name: string | null;
  title: string | null;
  quote_type: string;
  offering_mode: string;
  category_keys: string[] | null;
  created_at: string;
  /** QUOTE-FAV-02 */
  template_owner: string | null;
  template_use_count: number;
  /** QUOTE-FAV-03: pushed to the whole team by its owner ("From the Office"). */
  template_shared: boolean;
  template_shared_by: string | null;
  /** Derived client-side: this favorite is mine (I can rename / replace / delete / share it). */
  mine: boolean;
}

/** My favorites + the ones the office shared with everyone (the RLS policy
 *  hides every other row; the filter keeps the intent explicit). Mine sort
 *  first, then the shared ones, each A→Z. */
export function useQuoteTemplates() {
  return useQuery({
    queryKey: ["quote_templates"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<QuoteTemplate[]> => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id ?? null;
      if (!uid) return [];
      const { data, error } = await supabase
        .from("pegasus_quote")
        .select("id, template_name, title, quote_type, offering_mode, category_keys, created_at, template_owner, template_use_count, template_shared, template_shared_by")
        .eq("is_template", true)
        .or(`template_owner.eq.${uid},template_shared.eq.true`)
        .order("template_name");
      if (error) throw error;
      const rows = ((data ?? []) as Array<Omit<QuoteTemplate, "mine">>).map((t) => ({ ...t, template_shared: !!t.template_shared, mine: t.template_owner === uid }));
      return rows.sort((a, b) => Number(b.mine) - Number(a.mine) || (a.template_name ?? "").localeCompare(b.template_name ?? ""));
    },
  });
}

/** QUOTE-FAV-03: push one of my favorites to the whole team, or take it back. */
export function useShareTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; shared: boolean; byName: string | null }) => {
      const { error } = await supabase
        .from("pegasus_quote")
        .update({ template_shared: v.shared, template_shared_by: v.shared ? v.byName : null, updated_at: new Date().toISOString() })
        .eq("id", v.id)
        .eq("is_template", true);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  });
}

/** Everything an option carries, for deep copies (lines + discounts + rebates). */
async function fetchOptionsDeep(quoteId: string) {
  const { data: opts, error } = await supabase
    .from("pegasus_quote_option")
    .select("*")
    .eq("quote_id", quoteId)
    .order("sort");
  if (error) throw error;
  const optRows = (opts ?? []) as Record<string, unknown>[];
  const ids = optRows.map((o) => o.id as string);
  const [lines, discs, rebs] = await Promise.all([
    ids.length ? supabase.from("pegasus_quote_line").select("*").in("option_id", ids).order("sort") : Promise.resolve({ data: [] }),
    ids.length ? supabase.from("pegasus_quote_discount").select("*").in("option_id", ids).order("sort") : Promise.resolve({ data: [] }),
    ids.length ? supabase.from("pegasus_quote_rebate").select("*").in("option_id", ids).order("sort") : Promise.resolve({ data: [] }),
  ]);
  return {
    options: optRows,
    lines: (lines.data ?? []) as Record<string, unknown>[],
    discounts: (discs.data ?? []) as Record<string, unknown>[],
    rebates: (rebs.data ?? []) as Record<string, unknown>[],
  };
}

/** Copy a quote's options (deep) onto another quote id. Strips row ids. */
async function copyOptionsDeep(fromQuoteId: string, toQuoteId: string) {
  const deep = await fetchOptionsDeep(fromQuoteId);
  for (const o of deep.options) {
    const { id: oldId, quote_id: _q, created_at: _c, updated_at: _u, ...rest } = o as { id: string; quote_id: string; created_at?: unknown; updated_at?: unknown } & Record<string, unknown>;
    const { data: created, error } = await supabase
      .from("pegasus_quote_option")
      .insert({ ...rest, quote_id: toQuoteId })
      .select("id")
      .single();
    if (error) throw error;
    const newId = (created as { id: string }).id;
    const strip = (r: Record<string, unknown>) => {
      const { id: _i, option_id: _o, created_at: _c2, updated_at: _u2, ...keep } = r as { id?: unknown; option_id?: unknown; created_at?: unknown; updated_at?: unknown } & Record<string, unknown>;
      return { ...keep, option_id: newId };
    };
    const myLines = deep.lines.filter((l) => l.option_id === oldId).map(strip);
    const myDiscs = deep.discounts.filter((d) => d.option_id === oldId).map(strip);
    const myRebs = deep.rebates.filter((r) => r.option_id === oldId).map(strip);
    if (myLines.length) { const { error: e } = await supabase.from("pegasus_quote_line").insert(myLines); if (e) throw e; }
    if (myDiscs.length) { const { error: e } = await supabase.from("pegasus_quote_discount").insert(myDiscs); if (e) throw e; }
    if (myRebs.length) { const { error: e } = await supabase.from("pegasus_quote_rebate").insert(myRebs); if (e) throw e; }
  }
}

/** "Save as Favorite" — the open build becomes a named preset, owned by me. */
export function useSaveAsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { quote: Quote; name: string; shared?: boolean; byName?: string | null }): Promise<string> => {
      const { data: u } = await supabase.auth.getUser();
      if (!u?.user?.id) throw new Error("Sign in again to save a Favorite.");
      const { data: t, error } = await supabase
        .from("pegasus_quote")
        .insert({
          is_template: true,
          template_name: v.name.trim(),
          title: v.name.trim(),
          quote_type: v.quote.quote_type,
          offering_mode: v.quote.offering_mode,
          category_keys: v.quote.category_keys,
          status: "draft",
          created_by: u.user.email ?? null,
          template_owner: u.user.id,
          // QUOTE-FAV-03: share at save time (the office pushing a set to the techs).
          template_shared: !!v.shared,
          template_shared_by: v.shared ? (v.byName ?? null) : null,
        })
        .select("id")
        .single();
      if (error) throw error;
      const templateId = (t as { id: string }).id;
      await copyOptionsDeep(v.quote.id, templateId);
      return templateId;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  });
}

/** QUOTE-FAV-02: rename one of my favorites. */
export function useRenameTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; name: string }) => {
      const name = v.name.trim();
      if (!name) throw new Error("Give the Favorite a name.");
      const { error } = await supabase
        .from("pegasus_quote")
        .update({ template_name: name, title: name, updated_at: new Date().toISOString() })
        .eq("id", v.id)
        .eq("is_template", true);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  });
}

/** QUOTE-FAV-02: "Replace With This Build" — the favorite's collections are
 *  swapped for the open quote's (name and owner stay). This is how a
 *  favorite gets EDITED: load it, adjust, replace. */
export function useReplaceTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { template: QuoteTemplate; quote: Quote }) => {
      const { error: de } = await supabase.from("pegasus_quote_option").delete().eq("quote_id", v.template.id);
      if (de) throw de;
      await copyOptionsDeep(v.quote.id, v.template.id);
      const { error } = await supabase
        .from("pegasus_quote")
        .update({
          quote_type: v.quote.quote_type,
          offering_mode: v.quote.offering_mode,
          category_keys: v.quote.category_keys,
          updated_at: new Date().toISOString(),
        })
        .eq("id", v.template.id)
        .eq("is_template", true);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  });
}

/** "Load Favorite" — replaces the open quote's options with the preset's.
 *  Customer, dispatch and status stay; shape (type/mode/slots) follows the preset. */
export function useApplyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { template: QuoteTemplate; targetQuoteId: string }) => {
      const { error: de } = await supabase.from("pegasus_quote_option").delete().eq("quote_id", v.targetQuoteId);
      if (de) throw de;
      await copyOptionsDeep(v.template.id, v.targetQuoteId);
      const { error } = await supabase
        .from("pegasus_quote")
        .update({
          quote_type: v.template.quote_type,
          offering_mode: v.template.offering_mode,
          category_keys: v.template.category_keys,
          updated_at: new Date().toISOString(),
        })
        .eq("id", v.targetQuoteId);
      if (error) throw error;
      // QUOTE-FAV-02: "used N×" in the menu — best effort, never blocks the fill
      // (RLS lets only the owner's row update; a shared one just doesn't count).
      await supabase
        .from("pegasus_quote")
        .update({ template_use_count: (v.template.template_use_count ?? 0) + 1 })
        .eq("id", v.template.id)
        .eq("is_template", true);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote"] });
      qc.invalidateQueries({ queryKey: ["quote_list"] });
      qc.invalidateQueries({ queryKey: ["quote_templates"] });
    },
  });
}

/** Delete a favorite — options first (their lines/discounts/rebates cascade),
 *  then the template row itself. */
export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error: oe } = await supabase.from("pegasus_quote_option").delete().eq("quote_id", id);
      if (oe) throw oe;
      const { error } = await supabase.from("pegasus_quote").delete().eq("id", id).eq("is_template", true);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  });
}

/** Re-bid: clone a quote into a fresh DRAFT (server copies options/lines/discounts). Returns the new id. */
export function useCloneQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const { data, error } = await supabase.rpc("quote_clone", { p_id: id });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote_list"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
    },
  });
}

// ---- Diagnostic fees (service quotes — the call fee charged on top, its own line) ----

export interface DiagnosticFee {
  id: string;
  label: string;
  amount: number;
  segment: string | null;
  zone: string | null;
  after_hours: boolean;
}

/** The diagnostic / service-call fees (resi + commercial), cheapest first — for the
 * "Add diagnostic" control on a service quote. Charged on top, as its own line. */
export function useDiagnosticFees() {
  return useQuery({
    queryKey: ["diagnostic_fees"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<DiagnosticFee[]> => {
      const { data, error } = await supabase
        .from("fee_item")
        .select("id,label,amount,segment,zone,after_hours")
        .in("section", ["residential_diag", "commercial_diag"])
        .neq("active", false)
        .order("amount");
      if (error) throw error;
      return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
        id: r.id as string,
        label: r.label as string,
        amount: Number(r.amount),
        segment: (r.segment as string) ?? null,
        zone: (r.zone as string) ?? null,
        after_hours: Boolean(r.after_hours),
      }));
    },
  });
}

// ---- Proposal activity (the lifecycle log behind the "activity report") ----

export interface QuoteEvent {
  id: string;
  quote_id: string;
  kind: string;
  summary: string | null;
  actor: string | null;
  created_at: string;
}

/** Every lifecycle event for a quote (created → sent → viewed → accepted), newest first. */
export function useQuoteEvents(quoteId: string | null) {
  return useQuery({
    queryKey: ["quote_events", quoteId],
    enabled: !!quoteId,
    staleTime: 10 * 1000,
    queryFn: async (): Promise<QuoteEvent[]> => {
      const { data, error } = await supabase
        .from("pegasus_quote_event")
        .select("*")
        .eq("quote_id", quoteId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as QuoteEvent[];
    },
  });
}
